import { useState, useEffect, useRef } from "react";
import CultureQuestionnaireView from "./CultureQuestionnaireView";
import DocumentsView from "./DocumentsView";
import NewPositionView from "./NewPositionView";
import PositionsView from "./PositionsView";
import DashboardView from "./DashboardView";
import NotificationsView from "./NotificationsView";
import { useClientTodos } from "./lib/todos";
import Sidebar from "./Sidebar";
import { supabase, isAdmin, loadUserClients, translations, errorBanner, linkBtn, fetchAll, SURFACE } from "./constants";
import CandidatesView from "./CandidatesView";
import QualFormView from "./QualFormView";
import IntakeFormView from "./IntakeFormView";
import ReferenceFormView from "./ReferenceFormView";
import CandidateDetailView from "./CandidateDetailView";
import MethodologyView from "./MethodologyView";
import AccountView from "./AccountView";
import AdminView from "./AdminView";
import ClientsView from "./ClientsView";
import CredentialsView from "./CredentialsView";
import LandingView from "./LandingView";
import OnboardingModal from "./OnboardingModal";
import ChatDrawer from "./ChatDrawer";
import WelcomeView from "./WelcomeView";
import GuideView from "./GuideView";
import { PageLoader } from "./LogoLoader";
import FeedbackModal from "./FeedbackModal";
import { identifyUser, resetAnalytics } from "./lib/analytics";
import { useIsMobile, MOBILE_TOP, MOBILE_BOTTOM } from "./lib/responsive";

export default function App() {
  const [lang, setLang] = useState("en");
  // Idioma actual para avisos que salen desde efectos (sin volver a lanzarlos al cambiarlo)
  const langRef = useRef(lang);
  langRef.current = lang;
  const isMobile = useIsMobile();
  const [view, setView] = useState("landing");
  // Al volver a entrar, hasta saber si hay sesión se enseña el cargador del
  // logo, no la portada (antes se veía un instante y luego saltaba a la Home)
  const [authChecked, setAuthChecked] = useState(false);
  // Cliente cuyas posiciones ya se han cargado, y si ya se sabe qué clientes
  // tiene el usuario: mientras no, la Home y Posiciones enseñan el cargador en
  // vez de salir vacías un momento
  const [loadedClientId, setLoadedClientId] = useState(null);
  const [clientsChecked, setClientsChecked] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userId, setUserId] = useState(null);
  const [notifRefreshKey] = useState(0);
  const [qualToken, setQualToken] = useState(null);
  const [intakeToken, setIntakeToken] = useState(null);
  const [refToken, setRefToken] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const navRestoredRef = useRef(false);
  // Clientes (solo admin): ficha abierta, y la que pide la URL ?client=ID
  // (enlace "Cliente: …" desde la ficha de persona de Database, en el portal).
  const [clientsFocusId, setClientsFocusId] = useState(null);
  const pendingClientRef = useRef(null);

  // Detect ?qual=TOKEN, ?intake=TOKEN or ?ref=TOKEN in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qual = params.get("qual");
    const intake = params.get("intake");
    const ref = params.get("ref");
    if (qual) setQualToken(qual);
    if (intake) setIntakeToken(intake);
    if (ref) setRefToken(ref);
    const cl = Number(params.get("client"));
    if (cl) pendingClientRef.current = cl;
  }, []);


  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthChecked(true);
      if (session) {
        setUserEmail(session.user.email);
        setUserId(session.user.id);
        identifyUser(session.user.email);
        setView("dashboard");
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setUserEmail(null);
        setUserId(null);
        resetAnalytics();
        setView("landing");
        navRestoredRef.current = false;
        setOnboardingChecked(false);
        setShowOnboarding(false);
        localStorage.removeItem("rutment_nav_state");
      } else {
        setUserEmail(session.user.email);
        setUserId(session.user.id);
        identifyUser(session.user.email);
        if (_event === "SIGNED_IN") {
          supabase.from("admin_notifications").insert({
            type: "login",
            message: `Login: ${session.user.email}`,
            candidate_name: null,
            position_title: null,
          });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Check if user has completed onboarding (run once per session, after userId is set)
  useEffect(() => {
    if (!userId) return;
    if (onboardingChecked) return;
    supabase.from("user_preferences")
      .select("onboarding_completed_at")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data || !data.onboarding_completed_at) {
          setShowOnboarding(true);
        }
        setOnboardingChecked(true);
      });
  }, [userId, onboardingChecked]);

  // "How it works" cada vez que se abre Nueva posición (decisión 09/10).
  // Si se llega desde el botón final del propio tour, no se vuelve a enseñar.
  const skipNextOnboarding = useRef(false);
  useEffect(() => {
    if (view !== "newPosition") return;
    if (skipNextOnboarding.current) { skipNextOnboarding.current = false; return; }
    setShowOnboarding(true);
  }, [view]);

  const handleOnboardingClose = async (completed) => {
    setShowOnboarding(false);
    if (completed && userId) {
      await supabase.from("user_preferences").upsert({
        user_id: userId,
        onboarding_completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
    }
  };
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [backView, setBackView] = useState("open");
  const [initialCandidateTab, setInitialCandidateTab] = useState(null);

  // Persist nav state across reloads (only when logged in, and only after restore attempt)
  useEffect(() => {
    if (!userEmail) return;
    if (view === "landing") return; // Don't persist the landing
    if (!navRestoredRef.current) return; // Wait for restore attempt first to avoid overwriting saved state
    const navState = {
      view,
      backView,
      initialCandidateTab,
      selectedPositionId: selectedPosition?.id || null,
      selectedCandidateId: selectedCandidate?.id || null,
    };
    localStorage.setItem("rutment_nav_state", JSON.stringify(navState));
  }, [view, backView, initialCandidateTab, selectedPosition, selectedCandidate, userEmail]);

  // Restore nav state once positions are loaded — defined further down, after openPositionsList declaration

  const [candidateStatuses, setCandidateStatuses] = useState({});
  const [photoUrls, setPhotoUrls] = useState({});
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const [calendlyLink, setCalendlyLink] = useState("https://calendly.com/borja-rutment/30min");
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [hasCultureDoc, setHasCultureDoc] = useState(false);

  // Chat (global state, drawer + unread badge from sidebar and pipeline)
  const [chatOpen, setChatOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false); // "Send feedback" (20/10)
  const [chatUnread, setChatUnread] = useState(0);

  const userIsAdmin = isAdmin(userEmail);

  // Reset all client-related state when user changes (prevents residual state from previous session)
  useEffect(() => {
    setClients([]);
    setSelectedClientId(null);
    setCompanyLogoUrl(null);
    setCalendlyLink("");
    setOpenPositionsList([]);
    setClosedPositionsList([]);
    setDeletedPositionsList([]);
    setLoadedClientId(null);
    setClientsChecked(false);
  }, [userEmail]);

  // Poll unread chat messages (refresh every 30s + when drawer closes)
  useEffect(() => {
    if (!selectedClientId) { setChatUnread(0); return; }
    const refreshUnread = async () => {
      const otherRole = userIsAdmin ? "client" : "admin";
      const readField = userIsAdmin ? "read_by_admin" : "read_by_client";
      const { count } = await supabase
        .from("recruiter_chat_messages")
        .select("id", { count: "exact", head: true })
        .eq("client_id", selectedClientId)
        .eq("sender_role", otherRole)
        .eq(readField, false);
      setChatUnread(count || 0);
    };
    refreshUnread();
    const interval = setInterval(refreshUnread, 30000);
    return () => clearInterval(interval);
  }, [selectedClientId, userIsAdmin, chatOpen]);

  const handleCloseChat = () => {
    setChatOpen(false);
    setChatUnread(0);
  };

  // Admin: load all clients for selector
  useEffect(() => {
    if (!userEmail || !userIsAdmin) return;
    supabase.from("clients").select("id, name, email, logo_url, calendly_link").then(({ data }) => {
      if (data && data.length > 0) {
        setClients(data);
        const stored = localStorage.getItem("rutment_selected_client_id");
        const storedId = stored ? parseInt(stored, 10) : null;
        const validStored = storedId && data.some(c => c.id === storedId) ? storedId : null;
        setSelectedClientId(prev => prev || validStored || data[0].id);
      }
      setClientsChecked(true);
    });
  }, [userEmail, userIsAdmin]);

  // Persist selected client across reloads (admin only)
  useEffect(() => {
    if (userIsAdmin && selectedClientId) {
      localStorage.setItem("rutment_selected_client_id", String(selectedClientId));
    }
  }, [userIsAdmin, selectedClientId]);

  // Non-admin: load user_clients associations and auto-select first client
  useEffect(() => {
    if (!userEmail || !userId || userIsAdmin) return;
    (async () => {
      const ids = await loadUserClients(userId);
      if (ids.length === 0) {
        // Cuenta sin acceso a la plataforma de empresa (p. ej. usuario del portal de candidatos)
        await supabase.auth.signOut();
        window.alert(langRef.current === "es"
          ? "Esta cuenta no tiene acceso a la plataforma de Rutment. Si eres candidato, entra en candidates.rutment.com"
          : "This account does not have access to the Rutment platform. If you are a candidate, please use candidates.rutment.com");
        return;
      }
      setClientsChecked(true);
      if (ids.length > 0) {
        setSelectedClientId(ids[0]);
        // Also load the client metadata (name, logo, calendly) for display
        const { data: clientData } = await supabase
          .from("clients")
          .select("id, name, logo_url, calendly_link")
          .in("id", ids);
        if (clientData) setClients(clientData);
      }
    })();
  }, [userEmail, userId, userIsAdmin]);

  // Fotos de los candidatos. Se piden cuando hay sesión (y otra vez si cambia
  // el usuario): la tabla solo se puede leer con sesión, y antes se pedían una
  // vez al abrir la web, así que al entrar desde el login llegaban vacías y las
  // fotos no salían hasta recargar (15/10).
  useEffect(() => {
    if (!userId) return;
    supabase.from("candidate_photos").select("candidate_id, photo_url").then(({ data }) => {
      if (!data) return;
      const map = {};
      data.forEach(row => { map[row.candidate_id] = row.photo_url; });
      setPhotoUrls(prev => ({ ...prev, ...map }));
    });
  }, [userId]);

  // Check if culture doc exists for current client
  useEffect(() => {
    if (!selectedClientId) return;
    supabase.from("culture_docs").select("id").eq("client_id", selectedClientId).maybeSingle()
      .then(({ data }) => setHasCultureDoc(!!data));
  }, [selectedClientId]);

  // Load company logo and calendly link when selected client changes
  useEffect(() => {
    if (!selectedClientId) return;
    setCompanyLogoUrl(null);
    setCalendlyLink("");
    supabase.from("clients").select("logo_url, calendly_link").eq("id", selectedClientId).single()
      .then(({ data }) => {
        if (data?.logo_url) setCompanyLogoUrl(data.logo_url);
        setCalendlyLink(data?.calendly_link || "");
      });
  }, [selectedClientId]);

  // Load candidate statuses from Supabase on mount
  useEffect(() => {
    // Paginado con orden fijo: a partir de 1.000 candidaturas se cortaba sin avisar (24/10)
    fetchAll(() => supabase.from("candidates").select("id, status").order("id")).then(data => {
      const map = {};
      data.forEach(row => { if (row.status !== "pending") map[row.id] = row.status; });
      if (Object.keys(map).length > 0) setCandidateStatuses(map);
    }).catch(e => console.error("candidate statuses:", e));
  }, []);
  const [seenReports, setSeenReports] = useState(new Set());
  const [openPositionsList, setOpenPositionsList] = useState([]);
  const [closedPositionsList, setClosedPositionsList] = useState([]);
  const [deletedPositionsList, setDeletedPositionsList] = useState([]);
  // Avisos del cliente (03/11): la campanita del menú y la pantalla Notificaciones
  const clientTodos = useClientTodos({ openPositionsList, candidateStatuses, selectedClientId, hasCultureDoc });

  // Restore nav state once positions are loaded (run once per session)
  useEffect(() => {
    if (navRestoredRef.current) return;
    if (!userEmail) return;
    if (openPositionsList.length === 0 && closedPositionsList.length === 0) return; // Wait for data
    const raw = localStorage.getItem("rutment_nav_state");
    if (!raw) { navRestoredRef.current = true; return; }
    try {
      const saved = JSON.parse(raw);
      if (!saved.view || saved.view === "landing") { navRestoredRef.current = true; return; }
      const allLists = [openPositionsList, closedPositionsList, deletedPositionsList];
      let foundPos = null, foundCand = null;
      if (saved.selectedPositionId) {
        for (const list of allLists) {
          foundPos = list.find(p => p.id === saved.selectedPositionId);
          if (foundPos) break;
        }
      }
      if (foundPos && saved.selectedCandidateId) {
        foundCand = (foundPos.candidates || []).find(c => c.id === saved.selectedCandidateId);
      }
      if (saved.view === "candidateDetail" && foundPos && foundCand) {
        setSelectedPosition(foundPos);
        setSelectedCandidate(foundCand);
        setBackView(saved.backView || "open");
        setInitialCandidateTab(saved.initialCandidateTab || null);
        setView("candidateDetail");
      } else if (saved.view === "candidates" && foundPos) {
        setSelectedPosition(foundPos);
        setBackView(saved.backView || "open");
        setView("candidates");
      } else if (["dashboard", "notifications", "open", "closed", "deleted", "positions", "newPosition", "cultureQuestionnaire", "documents", "references", "support", "account", "admin", "clients", "credentials"].includes(saved.view)) {
        // Back-compat: map open/closed/deleted aliases to "positions" + persist activeTab
        if (saved.view === "open" || saved.view === "closed" || saved.view === "deleted") {
          try { localStorage.setItem("rutment_positions_tab", saved.view); } catch (e) {}
          setView("positions");
        } else {
          setView(saved.view);
        }
      }
    } catch (e) {
      console.error("Error restoring nav state:", e);
    }
    navRestoredRef.current = true;
  }, [userEmail, openPositionsList, closedPositionsList, deletedPositionsList]);

  // Load positions from Supabase — admin sees selected client; non-admin sees only their client(s)
  useEffect(() => {
    if (!userEmail) return;
    const loadPositions = async () => {
      let clientId = null;
      if (userIsAdmin) {
        if (!selectedClientId) return;
        clientId = selectedClientId;
      } else {
        // Non-admin: must have at least one user_clients row
        if (!selectedClientId) return;
        clientId = selectedClientId;
      }
      const { data, error } = await supabase.from("positions").select("*").eq("client_id", clientId);
      // Con error también se da por cargado: mejor la pantalla vacía que el cargador para siempre
      if (error || !data) { setLoadedClientId(clientId); return; }
      const positions = data.map(row => ({
        id: row.id,
        title: row.title,
        candidates: [],
        dateOpening: row.created_at ? new Date(row.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "",
        dateClosed: row.date_closed || null,
        metrics: { considered: 0, interested: row.interested || 0, qualified: 0, validated: 0, pending: 0, live: 0 },
        score: 0,
        jobDescription: row.job_description || "",
        cultureDoc: row.culture_doc || "",
        formData: row.form_data || {},
        // Rango salarial publicado por el admin (lo único salarial que ve el cliente)
        salaryReco: row.salary_reco || null,
        status: row.status || "open",
        clientId: row.client_id,
        // Orden manual de las abiertas (arrastrar en Posiciones). Sin valor =
        // posición nueva o aún no ordenada: va arriba, la más reciente primero.
        sortOrder: row.sort_order ?? null,
        // Preguntas de evaluación propias de la posición (null = las 8 por defecto)
        evalQuestions: row.eval_questions || null,
        // Mapping aprobado (la Home avisa si hay uno por aprobar)
        mappingApprovedAt: row.mapping_approved_at || null,
        createdAt: row.created_at || "",
      }));
      const byManualOrder = (a, b) => {
        const an = a.sortOrder === null, bn = b.sortOrder === null;
        if (an !== bn) return an ? -1 : 1;
        if (!an && a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return String(b.createdAt).localeCompare(String(a.createdAt));
      };
      const openPos = positions.filter(p => p.status === "open").sort(byManualOrder);
      const closedPos = positions.filter(p => p.status === "closed");
      const deletedPos = positions.filter(p => p.status === "deleted");

      // Load candidates filtered by client_id (security: never load other clients' candidates)
      // Paginado como los estados: un cliente con más de 1.000 candidaturas se cortaría
      let candData = null;
      try { candData = await fetchAll(() => supabase.from("candidates").select("*").eq("client_id", clientId).order("id")); }
      catch (e) { console.error("candidates:", e); }
      if (candData && candData.length > 0) {
        const byPos = {};
        candData.forEach(row => {
          const c = {
            id: row.id, name: row.name, company: row.company || "",
            position: row.current_position || "",
            city: row.city || "", salary: row.salary || "", notice: row.notice || "",
            // Años de experiencia (31/10): eje X de «Years vs. salary» en Comparar
            yearsExperience: row.years_experience ?? null,
            appDate: row.app_date || "", status: row.status || "pending",
            phone: row.phone || "", email: row.email || "", linkedin: row.linkedin || "",
            scoring: row.scoring || { technical: 0, soft: 0, cultural: 0, growth: 0 },
            verdict: row.verdict || "", strengths: row.strengths || [],
            weaknesses: row.weaknesses || [], qualSections: row.qual_sections || [],
            refsData: row.refs_data || [], references: row.references_count || 0,
            refPct: row.ref_pct || null, refInsights: row.ref_insights || null,
            referenceQuestions: row.reference_questions || null, evalManagers: row.eval_managers || [],
            evalScores: row.eval_scores || {}, evalNotes: row.eval_notes || {}, evalUpdatedAt: row.eval_updated_at || null,
            evalAiInsights: row.eval_ai_insights || null,
            interviewerGuide: row.interviewer_guide || null,
            processStatus: row.process_status || "receiving_applications",
            cvUrl: row.cv_url || null,
            createdBy: row.created_by || null,
            validatedBy: row.validated_by || null,
            // Sin esta línea la estrella se guarda pero no se vuelve a leer al
            // recargar: el objeto se construye con una lista fija de campos y
            // cualquier columna nueva que falte aquí se pierde en silencio.
            starred: row.starred || false,
            // Orden manual dentro de su columna del Tablero (null = sin ordenar)
            boardOrder: row.board_order ?? null,
            // Fechas de evento (trigger stamp_candidate_events): las usa el feed de la
            // Home. Sin estas líneas el feed nunca enseñaba cuándo pasó nada.
            qualifiedAt: row.qualified_at || null,
            validatedAt: row.validated_at || null,
            referencesReadyAt: row.references_ready_at || null,
            rejectedBy: row.rejected_by || null,
          };
          if (!byPos[row.position_id]) byPos[row.position_id] = [];
          byPos[row.position_id].push(c);
        });
        setOpenPositionsList(openPos.map(pos => ({ ...pos, candidates: byPos[pos.id] || [] })));
        setClosedPositionsList(closedPos.map(pos => ({ ...pos, candidates: byPos[pos.id] || [] })));
        setDeletedPositionsList(deletedPos.map(pos => ({ ...pos, candidates: byPos[pos.id] || [] })));
      } else {
        setOpenPositionsList(openPos);
        setClosedPositionsList(closedPos);
        setDeletedPositionsList(deletedPos);
      }
      setLoadedClientId(clientId);
    };
    loadPositions();
  }, [userEmail, selectedClientId, userIsAdmin]);
  const t = translations[lang];

  // Cerrar, borrar y reabrir: la pantalla cambia al momento y, si la base no
  // lo guarda (error o 0 filas por RLS), se deshace y se avisa arriba (23/10).
  // Antes solo iba a la consola: al recargar, la posición volvía a su sitio.
  const [positionError, setPositionError] = useState("");
  const persistPosition = (pos, fields, undo, onOk = () => {}) => {
    setPositionError("");
    supabase.from("positions").update(fields).eq("id", pos.id).select("id").then(({ data, error }) => {
      if (error || !data || data.length === 0) {
        undo();
        setPositionError(lang === "es" ? `No se pudo guardar el cambio en «${pos.title}». Inténtalo de nuevo.` : `Couldn't save the change to “${pos.title}”. Please try again.`);
      } else onOk();
    });
  };

  // A quién se contrató al cerrar (23/10): un "Resultado" en Track de esa
  // persona, enlazado a la posición y al cliente. Solo admin (outcomes es solo
  // admin). Si la candidatura no tiene persona, se dice y se apunta a mano.
  const recordHire = async (pos, hire) => {
    if (!hire?.candidateId) return;
    const cand = (pos.candidates || []).find(c => String(c.id) === String(hire.candidateId));
    const { data: row } = await supabase.from("candidates").select("person_id").eq("id", String(hire.candidateId)).maybeSingle();
    if (!row?.person_id) {
      setPositionError(lang === "es" ? `Posición cerrada, pero ${cand?.name || "el candidato"} no está enlazado a una persona: apunta la contratación en su Track desde Database.` : `Position closed, but ${cand?.name || "the candidate"} isn't linked to a person: record the hire in their Track from Database.`);
      return;
    }
    const note = `Contratado/a para «${pos.title}»${hire.note?.trim() ? ` · ${hire.note.trim()}` : ""}`;
    const { data, error } = await supabase.from("outcomes").insert({
      person_id: row.person_id, kind: "result", note, occurred_on: new Date().toISOString().slice(0, 10),
      position_id: pos.id, client_id: pos.clientId ?? null,
    }).select("id");
    if (error || !data?.length) setPositionError(lang === "es" ? `Posición cerrada, pero no se pudo guardar la contratación en Track.` : `Position closed, but the hire couldn't be saved to Track.`);
  };

  const handleClosePosition = (pos, hire = null) => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yy = String(today.getFullYear()).slice(2);
    const dateStr = `${dd}/${mm}/${yy}`;
    const closedPos = { ...pos, dateClosed: dateStr, metrics: { ...pos.metrics, pending: 0, live: 0 } };
    setOpenPositionsList(prev => prev.filter(p => p.id !== pos.id));
    setClosedPositionsList(prev => [closedPos, ...prev]);
    persistPosition(pos, { status: "closed", date_closed: dateStr }, () => {
      setClosedPositionsList(prev => prev.filter(p => p.id !== pos.id));
      setOpenPositionsList(prev => [pos, ...prev]);
    }, () => recordHire(pos, hire));
  };

  const handleDeletePosition = (pos) => {
    setOpenPositionsList(prev => prev.filter(p => p.id !== pos.id));
    setDeletedPositionsList(prev => [{ ...pos, status: "deleted" }, ...prev]);
    persistPosition(pos, { status: "deleted" }, () => {
      setDeletedPositionsList(prev => prev.filter(p => p.id !== pos.id));
      setOpenPositionsList(prev => [pos, ...prev]);
    });
  };

  const handleReopenPosition = (pos) => {
    const reopened = { ...pos, status: "open", dateClosed: null };
    setClosedPositionsList(prev => prev.filter(p => p.id !== pos.id));
    setDeletedPositionsList(prev => prev.filter(p => p.id !== pos.id));
    setOpenPositionsList(prev => [reopened, ...prev]);
    persistPosition(pos, { status: "open" }, () => {
      setOpenPositionsList(prev => prev.filter(p => p.id !== pos.id));
      if (pos.status === "deleted") setDeletedPositionsList(prev => [pos, ...prev]);
      else setClosedPositionsList(prev => [pos, ...prev]);
    });
  };

  // Reordenar las posiciones abiertas. Se guarda el índice de cada una en
  // positions.sort_order y se comprueba que de verdad se escribió: un update
  // bloqueado por RLS devuelve 200 con 0 filas. Si algo falla, se deshace y
  // devuelve false para que la pantalla lo diga.
  const handleReorderPositions = async (ordered) => {
    const before = openPositionsList;
    const next = ordered.map((p, i) => ({ ...p, sortOrder: i }));
    setOpenPositionsList(next);
    const changed = next.filter((p, i) => before.findIndex(b => b.id === p.id) !== i || before[i]?.sortOrder !== i);
    const results = await Promise.all(changed.map(p =>
      supabase.from("positions").update({ sort_order: p.sortOrder }).eq("id", p.id).select("id")
    ));
    const failed = results.some(({ data, error }) => error || !data || data.length === 0);
    if (failed) { setOpenPositionsList(before); return false; }
    return true;
  };

  // La posición abierta, leída de las listas y no de la copia guardada al
  // entrar: así lo que se cambia dentro (candidato nuevo, evaluación…) se ve
  // sin salir y volver a entrar.
  const livePosition = selectedPosition
    ? ([...openPositionsList, ...closedPositionsList, ...deletedPositionsList].find(p => p.id === selectedPosition.id) || selectedPosition)
    : null;

  const goToPosition = (pos, from) => {
    setSelectedPosition(pos);
    setBackView(from);
    setView("candidates");
  };

  const goToCandidate = (candidate, position, initialTab, fromView) => {
    setSelectedPosition(position);
    setBackView(fromView || "open");
    setSelectedCandidate(candidate);
    setInitialCandidateTab(initialTab || null);
    setView("candidateDetail");
  };

  // ?client=ID: solo para admin. Gana a la navegación guardada.
  useEffect(() => {
    if (!userIsAdmin || !pendingClientRef.current) return;
    navRestoredRef.current = true;
    setClientsFocusId(pendingClientRef.current);
    pendingClientRef.current = null;
    setView("clients");
    window.history.replaceState(null, "", window.location.pathname);
  }, [userIsAdmin, userEmail]);

  // Resolved company name for display (works for admin and non-admin)
  const resolvedCompanyName = clients.find(c => c.id === selectedClientId)?.name || "";

  // Pantallas que dependen de las posiciones del cliente: hasta que llegan,
  // cargador del logo en vez de una Home o una lista vacías
  const positionsPending = !!userEmail && (selectedClientId == null ? !clientsChecked : loadedClientId !== selectedClientId);
  const POSITION_VIEWS = ["dashboard", "notifications", "positions", "open", "closed", "deleted", "candidates", "candidateDetail"];

  const renderView = () => {
    if (positionsPending && POSITION_VIEWS.includes(view)) return <PageLoader fixed appearDelay={null} />;
    switch (view) {
      case "landing": return <LandingView setView={setView} lang={lang} setLang={setLang} setUserEmail={setUserEmail} />;
      case "dashboard": return <DashboardView key={selectedClientId} t={t} lang={lang} photoUrls={photoUrls} candidateStatuses={candidateStatuses} setView={setView} setSelectedPosition={setSelectedPosition} setSelectedCandidate={setSelectedCandidate} setBackView={setBackView} goToCandidate={goToCandidate} openPositionsList={openPositionsList} closedPositionsList={closedPositionsList} isAdmin={userIsAdmin} selectedClientId={selectedClientId} calendlyLink={calendlyLink} hasCultureDoc={hasCultureDoc} />;
      case "notifications": return <NotificationsView lang={lang} items={clientTodos} setView={setView} setSelectedPosition={setSelectedPosition} goToCandidate={goToCandidate} openPositionsList={openPositionsList} selectedClientId={selectedClientId} photoUrls={photoUrls} />;
      case "open":
      case "closed":
      case "deleted":
      case "positions": {
        const initialTab = view === "closed" ? "closed" : view === "deleted" ? "deleted" : view === "open" ? "open" : null;
        return <PositionsView t={t} lang={lang} setView={setView} setSelectedPosition={(pos) => goToPosition(pos, "positions")} isAdmin={userIsAdmin} candidateStatuses={candidateStatuses} openPositionsList={openPositionsList} closedPositionsList={closedPositionsList} deletedPositionsList={deletedPositionsList} onClosePosition={handleClosePosition} onDeletePosition={handleDeletePosition} onReopenPosition={handleReopenPosition} onReorderPositions={handleReorderPositions} setOpenPositionsList={setOpenPositionsList} initialTab={initialTab} />;
      }
      case "candidates": return <CandidatesView t={t} lang={lang} position={livePosition} setView={setView} setSelectedCandidate={setSelectedCandidate} backView={backView} candidateStatuses={candidateStatuses} setCandidateStatuses={setCandidateStatuses} setInitialCandidateTab={setInitialCandidateTab} setBackView={setBackView} seenReports={seenReports} photoUrls={photoUrls} isAdmin={userIsAdmin} setOpenPositionsList={setOpenPositionsList} companyLogoUrl={companyLogoUrl} companyName={resolvedCompanyName} activeClientId={selectedClientId} userId={userId} calendlyLink={calendlyLink} chatUnread={chatUnread} onOpenChat={() => setChatOpen(true)} setPhotoUrls={setPhotoUrls} />;
      case "candidateDetail": return <CandidateDetailView t={t} lang={lang} candidate={selectedCandidate} position={livePosition} setView={setView} candidateStatuses={candidateStatuses} setCandidateStatuses={setCandidateStatuses} initialTab={initialCandidateTab} backView={backView} seenReports={seenReports} setSeenReports={setSeenReports} photoUrls={photoUrls} setPhotoUrls={setPhotoUrls} isAdmin={userIsAdmin} userId={userId} onUpdateCandidate={(updated) => { setSelectedCandidate(updated); setOpenPositionsList(prev => prev.map(pos => ({ ...pos, candidates: (pos.candidates || []).map(c => c.id === updated.id ? updated : c) }))); }} onDeleteCandidate={(gone) => { const drop = list => list.map(pos => ({ ...pos, candidates: (pos.candidates || []).filter(c => String(c.id) !== String(gone.id)) })); setOpenPositionsList(drop); setClosedPositionsList(drop); setSelectedCandidate(null); }} calendlyLink={calendlyLink} companyName={resolvedCompanyName} />;
      case "newPosition": return <NewPositionView t={t} lang={lang} setView={setView} selectedClientId={selectedClientId} onPositionCreated={(pos) => setOpenPositionsList(prev => [pos, ...prev])} isAdmin={userIsAdmin} />;
      case "cultureQuestionnaire": return <CultureQuestionnaireView t={t} lang={lang} setView={setView} selectedClientId={selectedClientId} companyName={resolvedCompanyName} onCultureDocSaved={() => setHasCultureDoc(true)} isAdmin={userIsAdmin} />;
      case "documents": return <DocumentsView t={t} lang={lang} setView={setView} selectedClientId={selectedClientId} companyName={resolvedCompanyName} openPositionsList={openPositionsList} companyLogoUrl={companyLogoUrl} isAdmin={userIsAdmin} />;
      case "support": return <MethodologyView t={t} lang={lang} onShowOnboarding={() => setShowOnboarding(true)} />;
      case "account": return <AccountView t={t} lang={lang} companyName={resolvedCompanyName} userEmail={userEmail} selectedClientId={selectedClientId} isAdmin={userIsAdmin} onCompanyNameChange={(newName) => setClients(prev => prev.map(c => c.id === selectedClientId ? { ...c, name: newName } : c))} />;
      case "admin": return <AdminView lang={lang} openPositionsList={openPositionsList} setOpenPositionsList={setOpenPositionsList} />;
      // Memoria de cliente: solo admin. A un cliente se le enseña su Home.
      case "clients": return userIsAdmin
        ? <ClientsView lang={lang} focusClientId={clientsFocusId} setFocusClientId={setClientsFocusId} onCalendlySaved={(id, link) => { if (id === selectedClientId) setCalendlyLink(link || ""); }} />
        : <DashboardView key={selectedClientId} t={t} lang={lang} photoUrls={photoUrls} candidateStatuses={candidateStatuses} setView={setView} setSelectedPosition={setSelectedPosition} setSelectedCandidate={setSelectedCandidate} setBackView={setBackView} goToCandidate={goToCandidate} openPositionsList={openPositionsList} closedPositionsList={closedPositionsList} isAdmin={userIsAdmin} selectedClientId={selectedClientId} calendlyLink={calendlyLink} hasCultureDoc={hasCultureDoc} />;
      case "credentials": return userIsAdmin
        ? <CredentialsView lang={lang} />
        : <DashboardView key={selectedClientId} t={t} lang={lang} photoUrls={photoUrls} candidateStatuses={candidateStatuses} setView={setView} setSelectedPosition={setSelectedPosition} setSelectedCandidate={setSelectedCandidate} setBackView={setBackView} goToCandidate={goToCandidate} openPositionsList={openPositionsList} closedPositionsList={closedPositionsList} isAdmin={userIsAdmin} selectedClientId={selectedClientId} calendlyLink={calendlyLink} hasCultureDoc={hasCultureDoc} />;
      default: return <DashboardView t={t} lang={lang} photoUrls={photoUrls} candidateStatuses={candidateStatuses} openPositionsList={openPositionsList} closedPositionsList={closedPositionsList} setView={setView} setSelectedPosition={setSelectedPosition} setSelectedCandidate={setSelectedCandidate} setBackView={setBackView} goToCandidate={goToCandidate} />;
    }
  };

  // Public links (intake / qual) always take precedence — no login required.
  // Páginas propias sin sesión: /welcome (poner la contraseña de una
  // invitación) y /guide (guía pública). Vercel sirve index.html en cualquier ruta.
  const path = window.location.pathname.replace(/\/+$/, "");
  if (path === "/welcome") return <WelcomeView />;
  if (path === "/guide") return <GuideView />;
  if (intakeToken) return <IntakeFormView token={intakeToken} />;
  if (refToken) return <ReferenceFormView token={refToken} />;
  if (qualToken) return <QualFormView token={qualToken} />;

  if (view === "landing" && !authChecked) return <PageLoader fixed background={SURFACE} />;
  if (view === "landing") {
    return (
      <>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Bebas+Neue&family=JetBrains+Mono:wght@700&display=swap" rel="stylesheet" />
        <LandingView setView={setView} lang={lang} setLang={setLang} setUserEmail={setUserEmail} />
      </>
    );
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: SURFACE, fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Bebas+Neue&family=JetBrains+Mono:wght@700&display=swap" rel="stylesheet" />
      <Sidebar notifRefreshKey={notifRefreshKey} view={view} setView={setView} t={t} lang={lang} setLang={setLang} companyName={resolvedCompanyName} candidateStatuses={candidateStatuses} openPositionsList={openPositionsList} seenReports={seenReports} setSeenReports={setSeenReports} isAdmin={userIsAdmin} companyLogoUrl={companyLogoUrl} setCompanyLogoUrl={setCompanyLogoUrl} clients={userIsAdmin ? clients : []} selectedClientId={selectedClientId} setSelectedClientId={setSelectedClientId} onShowOnboarding={() => setShowOnboarding(true)} onFeedback={() => setShowFeedback(true)} chatUnread={chatUnread} notifCount={clientTodos.length} onContactRecruiter={() => setChatOpen(true)} />
      {/* Altura fija: así el que se desplaza es <main> y no la ventana. Sin
          esto, overflowY no servía de nada y position: sticky no funcionaba
          en ninguna pantalla (el formulario fijo de Evaluación, por ejemplo). */}
      <main style={{ flex: 1, overflowY: "auto", overflowX: "hidden", height: isMobile ? "100dvh" : "100vh", minWidth: 0, boxSizing: "border-box",
        // Hueco de la barra de scroll siempre reservado: si no, al pasar de una
        // pantalla corta a una larga aparecía la barra y todo se movía (24/10)
        scrollbarGutter: "stable",
        // Móvil: hueco para la barra inferior (Sidebar.jsx). El de la barra
        // superior NO va como relleno: Safari calcula las cabeceras fijas
        // (sticky) desde dentro del relleno y se quedaban 52 px más abajo,
        // con el contenido asomando por encima (24/10). Va como bloque vacío.
        ...(isMobile ? { paddingBottom: `calc(${MOBILE_BOTTOM + 8}px + env(safe-area-inset-bottom))` } : null) }}>
        {isMobile && <div aria-hidden="true" style={{ height: `calc(${MOBILE_TOP}px + env(safe-area-inset-top))` }} />}
        {positionError && (
          <div style={{ ...errorBanner, margin: isMobile ? "12px 16px 0" : "16px 32px 0", display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span>{positionError}</span>
            <button onClick={() => setPositionError("")} style={{ ...linkBtn, color: "inherit" }}>{lang === "es" ? "Cerrar" : "Close"}</button>
          </div>
        )}
        {renderView()}
      </main>
      {showFeedback && (
        <FeedbackModal lang={lang} page={view + (selectedPosition && ["candidates", "candidateDetail"].includes(view) ? ` · ${selectedPosition.title}` : "")} onClose={() => setShowFeedback(false)} />
      )}
      {showOnboarding && (
        <OnboardingModal
          lang={lang}
          onClose={handleOnboardingClose}
          onCTA={() => { skipNextOnboarding.current = true; setView("newPosition"); }}
        />
      )}
      <ChatDrawer
        open={chatOpen}
        onClose={handleCloseChat}
        clientId={selectedClientId}
        userEmail={userEmail}
        isAdmin={userIsAdmin}
        lang={lang}
      />
    </div>
  );
}
